/**
 * Copyright (c) 2023 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import React, { useRef } from 'react';
import '@adyen/adyen-web/dist/adyen.css';
import AdyenCheckout from '@adyen/adyen-web';
import { Box, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCreditCardCheckout } from './useCreditCardCheckout';
import CreditCardFormSkeleton from './CreditCardFormSkeleton';

/**
 * AdyenCheckout Component
 * @returns {JSX.Element}
 * @constructor
 */
export function CreditCardCheckout() {
  const paymentContainer = useRef(null);
  const {
    getClientConfiguration, isLoading, adyenPaymentState, isPaymentRedirect,
  } = useCreditCardCheckout();
  const { t } = useTranslation();

  /**
     * Render payment state messages
     * @returns {React.JSX.Element|null}
     */
  const renderMessages = () => {
    if (adyenPaymentState === 'PAYMENT_ERROR') {
      return <Box p={4}>{t('ERROR_MESSAGE_CREDIT_CARD_PAYMENT')}</Box>;
    }

    if (adyenPaymentState === 'PAYMENT_COMPLETE') {
      return <Box p={4}>{t('CREDIT_CARD_PAYMENT_COMPLETE')}</Box>;
    }
    return null;
  };

  /**
     * Render the adyen checkout form
     * @returns {JSX.Element}
     */
  const renderCheckout = () => {
    /**
         * Show credit card skeleton while paymentMethods are loading
         */
    if (isLoading) {
      return <CreditCardFormSkeleton />;
    }

    /**
         * Hide checkout when payment state contains an error, is complete or the user is redirected back from validation
         */
    if (isPaymentRedirect || adyenPaymentState === 'PAYMENT_ERROR' || adyenPaymentState === 'PAYMENT_COMPLETE') {
      return null;
    }

    // Only create adyenCheckout once
    if (!paymentContainer.current) {
      new AdyenCheckout(getClientConfiguration()).then((adyenCheckout) => adyenCheckout.create('card').mount(paymentContainer.current));
    }

    return (
      <Grid item xs={12}>
        <Box ref={paymentContainer} className="payment" />
      </Grid>
    );
  };

  return (
    <Grid container sx={{ marginTop: '12px' }} spacing={2} className="payment-container">
      {renderCheckout()}
      {renderMessages()}
    </Grid>
  );
}
